import React from 'react'
import { T, TeamLogo, TeamTag, TIER, pct, probColor, confColor, edgeColor, gradeColor, gradeLetter, tierFromProb, RatingBadge, Stat, GradeRing, VerdictChip, BET_VLEVEL, bookName, fmtOdds, Avatar, WeatherChip, WeatherBadge, playColor } from './ui'
import { playText, teamNick } from './plain'

// Betting spread for a team: nflverse spread_line > 0 = HOME favored, so the
// home team's line is -spread_line and the away team's is +spread_line (the
// favorite shows a minus). v is already signed for the team being shown.
const spreadLine = (abbr, v) => v == null ? teamNick(abbr) : `${teamNick(abbr)} ${v > 0 ? '+' : ''}${v}`

const STATUS = {
  active:       { color:T.green, label:'✓ Active' },
  questionable: { color:T.amber, label:'⚠ Questionable' },
  doubtful:     { color:T.orange,label:'⚠ Doubtful' },
  out:          { color:T.red,   label:'✕ Out' },
}

// Plain-English read of the model's stance — so a user doesn't misread a shiny % on a
// play the model has actually buried (e.g. a LOW-tier committee-back TD). Driven by the
// SAME tier the badge shows, plus an early-season caveat when the number is riding last
// year's role (carryover, before this season's usage exists).
export function plainRead(tier, isTD, carrySeason){
  const MAP = {
    "ELITE":    "The model's top tier — its highest-confidence plays.",
    "BEST BET": "The model's top tier — its highest-confidence plays.",
    "STRONG":   "A confident lean from the model.",
    "PLAY":     "A modest lean — playable, not a smash.",
    "WATCH":    "Borderline — worth watching, not a strong play.",
    "PASS":     `A low-confidence floor ${isTD?'dart':'play'} — the model isn't buying it.`,
  }
  let s = MAP[tier] || MAP["PASS"]
  if(carrySeason) s += ` Early-season read — based on ${carrySeason} role, not confirmed usage this year.`
  return s
}

// A play makes the "strong case" cut when the board's Case verdict is Strong or
// Leans yes — the single shared definition of "strong" across the app (board chip,
// strong-only filter, and the Home survey), so the word means one thing everywhere.
export const isStrongCase = e => !!(e?.case && (e.case.verdict === 'Strong' || e.case.verdict === 'Leans yes'))

// Last-N hit sparkline — did he clear THIS line recently? Green tall bar = over, faint
// short bar = under, oldest → newest (the PFF card's hit-rate strip, on our data).
function HitBars({ hist }){
  const h = hist && hist.hist
  if(!h || !h.length) return <span style={{fontSize:11,color:T.faint}}>no logs</span>
  return (
    <span style={{display:"inline-flex",alignItems:"flex-end",gap:2,height:15}}>
      {h.map((v,i)=>(
        <span key={i} style={{width:3,height:v?14:6,borderRadius:1,background:v?T.green:T.faint,opacity:v?0.95:0.7}}/>
      ))}
    </span>
  )
}

// Compact labeled cell for the card's scan row.
function MiniCard({ label, children }){
  return (
    <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:9,padding:"7px 10px",minWidth:0}}>
      <div style={{fontSize:10,color:T.muted,letterSpacing:"0.05em",marginBottom:4,textTransform:"uppercase"}}>{label}</div>
      {children}
    </div>
  )
}

// Positional matchup rating from the model's defense adjustment (>1 = soft spot).
function matchupRating(e){
  const dm = e.components && e.components["Defense matchup"]
  if(dm==null) return { label:"—", color:T.muted }
  if(dm>=1.04) return { label:"GOOD", color:T.green }
  if(dm<=0.96) return { label:"TOUGH", color:T.red }
  return { label:"FAIR", color:T.muted2 }
}

// "The Case" chip — now a worded VERDICT (Strong / Leans yes / Mixed / Tough /
// Priced out) plus the greens/concerns tally, using the same VerdictChip the
// Fantasy tab uses so bets and lineups speak one visual language. Full factor
// list lives in the player modal.
function CaseChip({ c }){
  if(!c) return null
  const tally = `${c.greens}✓${c.reds>0?` ${c.reds}✕`:''}`
  return <VerdictChip label={c.verdict} level={BET_VLEVEL[c.verdict]||'neutral'} sub={tally}
    title={`The Case — ${c.verdict}: ${c.greens} for${c.reds?`, ${c.reds} against`:''}`} />
}

// Matches the MLB UnifiedApp PickCard layout exactly (rank, name, tier, score
// strip of Prob/Confidence/Edge/Line, context row, data-quality strip).
// Live in-game progress strip — shown only while a player's game is live/final and the
// backend attached e.live (current stat vs the shown line). Counting props get a progress
// bar; anytime-TD shows scored / not yet. Pure inline styles (no keyframes) so it renders
// anywhere. Absent → the card looks exactly as it did pregame.
function LiveStrip({ e, isTD }){
  const lv = e.live; if(!lv) return null
  const done = lv.state === 'post'
  const color = lv.status==='hit' ? T.green : lv.status==='miss' ? T.red : T.text
  const pct = (!isTD && lv.line>0) ? Math.max(0, Math.min(1, lv.actual/lv.line)) : (lv.cleared?1:0)
  const label = isTD
    ? (lv.cleared ? 'Scored ✓' : done ? 'Did not score' : 'No TD yet')
    : `${lv.actual} / ${lv.line}${lv.status==='hit'?' ✓':''}`
  const tag = lv.status==='hit' ? 'HIT' : lv.status==='miss' ? 'MISS' : (done ? 'FINAL' : 'LIVE')
  const barColor = lv.status==='miss' ? T.red : T.green
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,padding:'7px 10px',borderRadius:8,
        background:lv.status==='hit'?T.greenDim:T.card3,border:`1px solid ${lv.status==='hit'?T.green:T.border}`}}>
      <span style={{display:'flex',alignItems:'center',gap:5,flexShrink:0}}>
        {!done && <span style={{width:7,height:7,borderRadius:'50%',background:T.green,display:'inline-block'}}/>}
        <span style={{fontSize:10,fontWeight:800,letterSpacing:'.05em',color}}>{tag}</span>
      </span>
      <span style={{fontSize:12.5,fontWeight:800,fontFamily:T.mono,color,flexShrink:0}}>{label}</span>
      {!isTD && (
        <span style={{flex:1,minWidth:30,height:5,borderRadius:3,background:T.border,overflow:'hidden'}}>
          <span style={{display:'block',height:'100%',width:`${pct*100}%`,background:barColor,borderRadius:3}}/>
        </span>
      )}
    </div>
  )
}

export function PickCard({ e, rank, onOpen, carrySeason }){
  const prob = e.prob_over||0
  const conf = e.confidence||0
  const edge = e.edge_pct
  const st = STATUS[e.status] || STATUS.active
  const isTD = e.market==='Anytime TD'
  // Once real odds are pulled, show the BOOK line (not the canonical model line) and
  // the model's hit% AT that line. Pre-odds we show the model's own reference line,
  // clearly labelled so it never reads as an unfilled book line.
  const hasBook = e.book_line!=null && !isTD
  const lineIsModel = !hasBook && !isTD          // a model reference line, no book price yet
  const lineLabel = isTD ? '0.5' : (hasBook ? e.book_line : e.line)
  // Headline hit% — the CANONICAL probability the board ranked this card by (backend
  // hit_prob: book-line prob when priced, else canonical), so the order can never
  // contradict the number shown. Fall back to the old derivation for older payloads.
  const hit = e.hit_prob!=null ? Math.round(e.hit_prob*100)
    : hasBook && e.book_prob_over!=null ? Math.round(e.book_prob_over*100)
    : e.impact?.model_pct!=null ? e.impact.model_pct : Math.round(prob*100)
  // Tier is derived from the SAME probability shown in the headline, so the badge
  // can never contradict the number (no ELITE on a 54% play).
  const tier = e.tier || tierFromProb(hit/100, conf, e.edge_pct, e.prop_type)
  return (
    <button onClick={()=>onOpen&&onOpen(e)} style={{display:"block",width:"100%",textAlign:"left",cursor:onOpen?"pointer":"default",color:T.text,background:T.card,border:`1px solid ${rank<=3?T.greenMid:T.border}`,borderRadius:12,padding:14,marginBottom:8}}>
      {/* Header — name + the headline hit% */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:5,gap:10}}>
        <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
          <RatingBadge tier={tier} size={34}/>
          <Avatar url={e.headshot} name={e.full_name} team={e.team} size={34}/>
          <span style={{fontSize:16,fontWeight:800,color:T.text,lineHeight:1.2,minWidth:0}}>{e.full_name} <span style={{fontSize:11,fontWeight:700,color:T.muted,whiteSpace:"nowrap"}}>{e.pos}</span></span>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontFamily:T.mono,fontWeight:900,fontSize:19,lineHeight:1,color:playColor(hit/100,e)}}>{hit}%</div>
          <div style={{fontSize:10,color:T.muted,letterSpacing:"0.04em"}}>{isTD?'TO SCORE':'TO HIT'}</div>
        </div>
      </div>
      {/* Bet + edge */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:10}}>
        <span style={{fontSize:11.5,color:T.muted,minWidth:0}}>
          {e.team} · vs {teamNick(e.opp)} · <b style={{color:T.text}}>{isTD?'Anytime TD':`${playText(e.market, lineLabel)}`}</b>
          {lineIsModel && (
            <span title="No posted book line yet — this is our PROJECTED line, not a book number"
              style={{marginLeft:6,fontSize:10,fontWeight:800,letterSpacing:"0.04em",color:T.amber,
                background:T.amberDim,border:`1px solid ${T.amber}`,borderRadius:4,padding:"1px 5px"}}>PROJ</span>
          )}
        </span>
        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
          {/* Suspect line — the posted number is implausibly far from our projection (bad
              feed). Hide the fabricated edge/Value and warn instead of advertising a play. */}
          {e.line_suspect && (
            <span title="This book's line disagrees with the rest of the market — likely a bad feed row, not a real edge"
              style={{fontSize:11,fontWeight:800,color:T.amber,background:T.amberDim,border:`1px solid ${T.amber}`,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"}}>⚠ Line looks off</span>
          )}
          {!e.line_suspect && e.case && <CaseChip c={e.case}/>}
          {!e.line_suspect && edge!=null && Math.abs(edge)>=0.5 && (
            <span style={{fontSize:11,fontWeight:800,color:edge>=0?T.green:T.red,background:edge>=0?T.greenDim:'transparent',border:`1px solid ${edge>=0?T.green:T.red}`,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"}}>{edge>=0?'+':''}{edge.toFixed(1)}% edge</span>
          )}
          {/* Value flag — shows when the model beats the posted price (+EV). It marks a
              play as beatable at the number; it does NOT suggest a wager size. */}
          {!e.line_suspect && e.value && (
            <span style={{fontSize:11,fontWeight:800,color:'#04140D',background:T.green,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"}}>✓ Value</span>
          )}
          {/* Steam — the market has moved toward our side since the open (live CLV). A
              market-movement signal, not a pick or a stake. */}
          {!e.line_suspect && e.steam_pts!=null && Math.abs(e.steam_pts)>=1.5 && (
            <span title="How far the market's fair line has moved since it opened — positive = toward our side"
              style={{fontSize:11,fontWeight:800,color:e.steam_pts>=0?T.green:T.amber,background:e.steam_pts>=0?T.greenDim:'transparent',border:`1px solid ${e.steam_pts>=0?T.green:T.amber}`,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"}}>📈 {e.steam_pts>=0?'+':''}{e.steam_pts} steam</span>
          )}
          {/* One conviction signal per card: the letter-grade ring (left) + the
              plain-English read below already say the tier — the duplicate word pill was
              a third, competing verdict, so it's gone. */}
        </div>
      </div>
      {/* Live in-game progress vs the line (only while the game is live/final) */}
      <LiveStrip e={e} isTD={isTD}/>
      {/* Plain-English read — what the tier actually MEANS, in words, so a shiny % on a
          buried play can't mislead. Colour-cued to the tier level. */}
      <div style={{fontSize:12,lineHeight:1.35,marginBottom:10,
          color:(tier==='PASS'||tier==='WATCH')?T.amber:(tier==='ELITE'||tier==='BEST BET'||tier==='STRONG')?T.green:T.muted}}>
        {plainRead(tier, isTD, carrySeason)}
      </div>
      {/* Scan row — positional matchup · recent hit history vs THIS line · projection.
          The PFF-style at-a-glance trio, on our model + real game logs. */}
      {(() => { const mr = matchupRating(e); const hh = e.hit_hist
        return (
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:8}}>
            <MiniCard label="Matchup"><span style={{fontSize:13,fontWeight:800,color:mr.color}}>{mr.label}</span></MiniCard>
            <MiniCard label={`Last ${hh?.n||10} · cleared`}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <HitBars hist={hh}/>
                {hh?.n ? <span style={{fontSize:12,fontFamily:T.mono,fontWeight:700,color:probColor((hh.w/hh.n))}}>{hh.w}/{hh.n}</span> : null}
              </div>
            </MiniCard>
            <MiniCard label="Projection"><span style={{fontSize:13,fontWeight:800,fontFamily:T.mono,color:T.text}}>{e.proj!=null?e.proj:'—'}</span></MiniCard>
          </div>
        )
      })()}
      {/* Supporting detail — confidence + the line */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8,marginBottom:10}}>
        <Stat label="Confidence" value={conf} color={confColor(conf)}/>
        <Stat label={hasBook?'Book line':lineIsModel?'Model line':'Line'} value={lineLabel}/>
      </div>
      {/* Suspect line — say plainly the posted number looks wrong vs our projection, so a
          bad feed never reads as a huge edge. */}
      {e.line_suspect && (
        <div style={{fontSize:11,color:T.amber,marginBottom:2,lineHeight:1.4}}>
          ⚠ This book's line (O{e.book_line}) disagrees with the rest of the market — likely a bad feed row, not a real edge. Verify before trusting it.
        </div>
      )}
      {/* Best price across books — line shopping. Once odds are pulled the line above
          IS the book line; show WHICH book has the best number + its American price. */}
      {hasBook && !e.line_suspect && (
        <div style={{fontSize:11,color:T.muted2,marginBottom:2,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
          <span style={{color:T.green,fontWeight:700}}>💰 Best {fmtOdds(e.book_over_odds)}</span>
          <span>{bookName(e.book_used)} · {playText(e.market, e.book_line)}</span>
          <span style={{color:T.faint}}>· tap for all books</span>
        </div>
      )}
      {/* Pre-odds: make it explicit this is the model's own reference line, not a
          posted book number, so it never looks like an unfilled placeholder. */}
      {lineIsModel && (
        <div style={{fontSize:11,color:T.faint,marginBottom:2}}>
          Model estimate for this player · book line not posted yet
        </div>
      )}
      {/* Context row */}
      {e.note && <div style={{fontSize:11,color:T.muted2,marginBottom:2}}>· {e.note}</div>}
      {/* Data quality strip — never bet an inactive player */}
      <div style={{background:T.card3,borderRadius:6,padding:"6px 10px",marginTop:8,fontSize:11,display:"flex",gap:12,flexWrap:"wrap"}}>
        <span style={{color:st.color}}>{st.label}</span>
        <span style={{color:T.muted}}>Status posts ~90 min pre-kick</span>
      </div>
    </button>
  )
}

// Compact one-line version of PickCard for dense lists (the Top N board on Home), so the
// page stays scannable instead of a wall of full cards. Same hit/tier/line derivation as
// PickCard so the number + badge always agree; tapping opens the full card/modal. Shows the
// scannable essentials: grade, name, bet, edge, unit, hit%.
export function CompactPickRow({ e, rank, onOpen }){
  const isTD = e.market === 'Anytime TD'
  const hasBook = e.book_line != null && !isTD
  const lineIsModel = !hasBook && !isTD
  const lineLabel = isTD ? '0.5' : (hasBook ? e.book_line : e.line)
  const hit = e.hit_prob != null ? Math.round(e.hit_prob * 100)
    : hasBook && e.book_prob_over != null ? Math.round(e.book_prob_over * 100)
    : Math.round((e.prob_over || 0) * 100)
  const tier = e.tier || tierFromProb(hit / 100, e.confidence || 0, e.edge_pct, e.prop_type)
  const edge = e.edge_pct
  return (
    <button onClick={() => onOpen && onOpen(e)} style={{ display:'flex', width:'100%', textAlign:'left',
      alignItems:'center', gap:9, cursor:onOpen?'pointer':'default', color:T.text, background:T.card,
      border:`1px solid ${rank<=3?T.greenMid:T.border}`, borderRadius:11, padding:'9px 11px', marginBottom:7 }}>
      <span style={{ fontFamily:T.mono, fontWeight:900, fontSize:12, color:rank<=3?T.green:T.faint, width:16, textAlign:'right', flexShrink:0 }}>{rank}</span>
      <RatingBadge tier={tier} size={30}/>
      <Avatar url={e.headshot} name={e.full_name || e.name} team={e.team} size={30}/>
      <div style={{ minWidth:0, flex:1 }}>
        <div style={{ fontSize:13.5, fontWeight:800, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {e.full_name || e.name} <span style={{ fontSize:10, color:T.muted, fontWeight:700 }}>{e.pos}</span>
        </div>
        <div style={{ fontSize:10.5, color:T.muted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginTop:1 }}>
          {isTD ? 'Anytime TD' : `${playText(e.market, lineLabel)}`}
          {lineIsModel && <span style={{ marginLeft:4, fontSize:10, fontWeight:800, color:T.amber, border:`1px solid ${T.amber}`, borderRadius:3, padding:'0 3px' }}>PROJ</span>}
          {e.opp ? ` · vs ${teamNick(e.opp)}` : ''}
        </div>
        {/* Weather bet badge — only when wind/cold actually moved this prop's number. */}
        {e.components && <WeatherBadge adj={e.components.Weather} style={{ marginTop:3 }} />}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
        {edge != null && <span style={{ fontFamily:T.mono, fontSize:11, fontWeight:700, color:edge>=0?T.green:T.red }}>{edge>=0?'+':''}{edge.toFixed(1)}%</span>}
        <div style={{ fontFamily:T.mono, fontWeight:900, fontSize:16, lineHeight:1, color:playColor(hit/100, e), minWidth:38, textAlign:'right' }}>{hit}%</div>
      </div>
    </button>
  )
}

// Compact "where the money is" line — spread/total MOVEMENT (open→now) + sharp (Pinnacle)
// lean, from /nfl/market. Renders nothing until there's real movement or a sharp split, so a
// quiet game stays clean. `m` is the per-game object from getNFLMarket (see GameLinesTab).
function LineMovement({ m }){
  if(!m) return null
  const sp=m.spread||{}, tot=m.total||{}
  const steam=m.signals?.includes('spread_steam')||m.signals?.includes('total_steam')
  const sharp=m.signals?.includes('sharp_spread_disagree')
  const sharpSide=(sp.sharp_gap!=null&&sp.sharp_gap!==0)?(sp.sharp_gap<0?m.home:m.away):null
  const parts=[]
  if(sp.move) parts.push(<span key="sp"><b style={{color:sp.move<0?T.green:T.amber}}>{sp.move<0?'▼':'▲'}{Math.abs(sp.move)}</b>{sp.money_side?` ${sp.money_side}`:''}</span>)
  if(tot.move) parts.push(<span key="ou">O/U <b style={{color:tot.move>0?T.green:T.amber}}>{tot.move>0?'▲':'▼'}{Math.abs(tot.move)}</b></span>)
  if(sharp&&sharpSide) parts.push(<span key="sh" style={{color:T.blue}}>◆ sharp {sharpSide}</span>)
  if(!parts.length) return null
  return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginTop:6,fontSize:10.5,color:T.muted,fontFamily:T.mono}}>
      <span style={{fontSize:10,fontWeight:800,color:T.faint,letterSpacing:"0.06em"}}>MONEY</span>
      {parts}
      {steam && <span style={{fontSize:10,fontWeight:800,color:T.green,border:`1px solid ${T.green}66`,borderRadius:999,padding:"1px 6px"}}>STEAM</span>}
    </div>
  )
}

// Public MONEY% (the handle) on the SPREAD and ML, straight on the card — Action Network
// money% per side via /nfl/market `splits`. Shows the side the money is on (bolded) and flags
// a sharp split (money% − tickets% gap ≥ 10) with ◆. Renders nothing until splits exist for
// the game, so cards without Action Network data stay clean. `m` is the per-game market object.
function MoneySplits({ m, away, home }){
  const s = m && m.splits
  if(!s) return null
  const pc = v => v==null ? '—' : v+'%'
  // one line: which side the money's on for a market, with the % and a sharp flag
  const line = (label, a, b, aLbl, bLbl) => {
    if(!a && !b) return null
    const leadA = (a?.money ?? -1) >= (b?.money ?? -1)
    const lead = leadA ? a : b, leadLbl = leadA ? aLbl : bLbl
    const big = lead?.gap != null && Math.abs(lead.gap) >= 10
    return (
      <div key={label} style={{display:"flex",alignItems:"center",gap:7,fontSize:10.5,padding:"1px 0"}}>
        <span style={{width:42,fontSize:10,fontWeight:800,color:T.faint,letterSpacing:"0.05em"}}>{label}</span>
        <span style={{fontFamily:T.mono}}>
          <b style={{color:big?T.blue:T.text}}>{leadLbl} {pc(lead?.money)}</b>
          <span style={{color:T.faint}}> money</span>
        </span>
        {big && <span style={{fontSize:10,fontWeight:800,color:T.blue}}>◆ sharp</span>}
      </div>
    )
  }
  const rows = [
    line('SPREAD', s.spread?.away, s.spread?.home, away, home),
    line('ML', s.moneyline?.away, s.moneyline?.home, away, home),
  ].filter(Boolean)
  if(!rows.length) return null
  return (
    <div style={{marginTop:6,paddingTop:6,borderTop:`1px solid ${T.border}`}}>
      <div style={{fontSize:10,fontWeight:800,color:T.green,letterSpacing:"0.06em",marginBottom:1}}>
        💵 PUBLIC MONEY{s.bet_count?<span style={{color:T.faint,fontWeight:600}}> · {Math.round(s.bet_count/1000)}k bets</span>:null}
      </div>
      {rows}
    </div>
  )
}

// Matches the MLB UnifiedApp GameCard layout (big abbrs, grade ring, action/env/
// plays row, best market, top play).
export function GameCard({ game, onOpen, market }){
  const indoor = game.roof==='dome' || game.roof==='closed'
  return (
    <button onClick={()=>onOpen&&onOpen(game)} style={{display:"block",width:"100%",textAlign:"left",cursor:onOpen?"pointer":"default",color:T.text,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px",marginBottom:8}}>
      {(game.time || game.state) && <div style={{fontSize:11,fontWeight:700,marginBottom:8}}>
        {game.state==='final'
          ? <span style={{color:T.muted2}}>● Final{game.away_score!=null&&game.home_score!=null?` · ${teamNick(game.away.abbr)} ${game.away_score}–${game.home_score} ${teamNick(game.home.abbr)}`:''}</span>
          : game.state==='live'
          ? <span style={{color:T.blue}}>● Live{game.time?` · ${game.day} ${game.time}`:''}</span>
          : <span style={{color:T.muted}}>🕒 {game.day} {game.time}</span>}
      </div>}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}><TeamLogo abbr={game.away.abbr} size={30}/><div><div style={{fontSize:10.5,color:T.muted}}>{game.away.city}</div><div style={{fontSize:17,fontWeight:900,letterSpacing:"-0.01em"}}>{teamNick(game.away.abbr)}</div></div></div>
          <span style={{fontSize:12,color:T.muted}}>@</span>
          <div style={{display:"flex",alignItems:"center",gap:8}}><TeamLogo abbr={game.home.abbr} size={30}/><div><div style={{fontSize:10.5,color:T.muted}}>{game.home.city}</div><div style={{fontSize:17,fontWeight:900,letterSpacing:"-0.01em"}}>{teamNick(game.home.abbr)}</div></div></div>
        </div>
        <div style={{textAlign:"center",flexShrink:0}}>
          <GradeRing grade={game.grade}/>
          <div style={{fontSize:10,fontWeight:800,color:T.muted,letterSpacing:".08em",marginTop:1}}>ACTION</div>
        </div>
      </div>
      {/* Projected touchdowns — the NFL analog of MLB's projected homers (expected TD
          scorers = sum of anytime-TD probs), with the most likely scorer. */}
      {game.proj_tds != null && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:11,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:9,padding:"8px 11px"}}>
          <span style={{fontSize:11.5,color:T.muted}}>🏈 Projected TDs{game.td_leader?<> · led by <b style={{color:T.text}}>{game.td_leader}</b></>:null}</span>
          <span style={{fontSize:20,fontWeight:900,fontFamily:T.mono,color:T.green,letterSpacing:"-0.02em"}}>{game.proj_tds.toFixed(1)}</span>
        </div>
      )}
      {/* Intelligence row */}
      <div style={{display:"flex",gap:8,marginTop:12}}>
        {/* Action = the over/under on a 30-95 scale (model total if no line yet) — say which. */}
        <span style={{flex:1,fontSize:11,fontWeight:700,color:T.text,background:T.greenDim,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",textAlign:"center"}}>
          {game.total!=null ? `${game.total} pts expected` : game.proj?.total!=null ? `~${Math.round(game.proj.total)} pts (model)` : 'No total yet'}</span>
        <span style={{flex:1,fontSize:11,fontWeight:700,color:T.muted2,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",textAlign:"center"}}>{game.bestBets??0} at 60%+</span>
        <span style={{flex:1,fontSize:11,fontWeight:700,color:T.muted2,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",textAlign:"center"}}>{game.plays??0} plays</span>
      </div>
      {/* Lines + venue */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,gap:8,fontSize:11,color:T.muted}}>
        <span>{spreadLine(game.away.abbr, game.spread)} · {spreadLine(game.home.abbr, game.spread!=null?-game.spread:null)} · O/U {game.total}</span>
        {game.weather
          ? <WeatherChip weather={game.weather} />
          : <span style={{color:T.muted2}}>{indoor?"🏟 Indoor":"🌬 Outdoor"} · {game.surface}</span>}
      </div>
      <LineMovement m={market}/>
      <MoneySplits m={market} away={game.away.abbr} home={game.home.abbr}/>
      {/* OUR CALL — the one-glance pick: who we think wins outright and who covers the
          spread, stated plainly and big. The straight-up winner and the ATS cover can
          differ (favorite wins but the underdog covers), so BOTH are spelled out instead
          of buried in pills. Projected score + total lean sit below as supporting detail. */}
      {game.proj && (() => { const p=game.proj
        const favHome=p.home_win_prob>=0.5, favAbbr=favHome?game.home.abbr:game.away.abbr
        const wp=Math.round((favHome?p.home_win_prob:1-p.home_win_prob)*100)
        const atsPick=p.ats_side!=null
          ? spreadLine(p.ats_side, p.ats_side===game.home.abbr ? (game.spread!=null?-game.spread:null) : game.spread)
          : null
        const atsBig=p.ats_edge!=null&&Math.abs(p.ats_edge)>=1.5
        const ouBig=p.ou_edge!=null&&Math.abs(p.ou_edge)>=2
        return (
        <div style={{marginTop:9,background:T.greenDim,border:`1px solid ${T.green}`,borderRadius:10,padding:"10px 12px"}}>
          <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.07em",color:T.green,marginBottom:7}}>🎯 OUR CALL</div>
          <div style={{display:"flex",alignItems:"stretch",gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,color:T.muted,letterSpacing:"0.05em",marginBottom:3}}>WINNER</div>
              <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                <span style={{fontSize:16,fontWeight:900,letterSpacing:"-0.01em",color:T.text}}><TeamTag abbr={favAbbr} size={20}/></span>
                <span style={{fontSize:12,fontFamily:T.mono,fontWeight:800,color:probColor(wp/100)}}>{wp}%</span>
              </div>
            </div>
            <div style={{width:1,background:T.border,flexShrink:0}}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,color:T.muted,letterSpacing:"0.05em",marginBottom:3}}>COVERS</div>
              <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                <span style={{fontSize:16,fontWeight:900,letterSpacing:"-0.01em",color:T.text}}>{atsPick||"—"}</span>
                {atsBig && <span style={{fontSize:10,fontFamily:T.mono,fontWeight:800,color:T.green}}>edge +{Math.abs(p.ats_edge)}</span>}
              </div>
            </div>
          </div>
          {/* Supporting detail — projected score, the O/U number, and the total lean */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginTop:9,paddingTop:8,borderTop:`1px solid ${T.border}`}}>
            <span style={{fontSize:10.5,fontFamily:T.mono,color:T.muted2}}>{teamNick(game.away.abbr)} {p.away_pts}–{p.home_pts} {teamNick(game.home.abbr)} · {p.total} pts</span>
            {p.ou_side!=null && p.ou_edge!=null && Math.abs(p.ou_edge)>=0.5 && <span style={{fontSize:10,fontWeight:800,color:ouBig?T.green:T.muted,background:ouBig?T.greenDim:"transparent",border:`1px solid ${ouBig?T.green:T.border}`,borderRadius:6,padding:"2px 7px"}}>{p.ou_side} {p.ou_edge>0?"+":""}{p.ou_edge}</span>}
          </div>
        </div>) })()}
      {/* Best market + top play */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:8,gap:8}}>
        <span style={{fontSize:11,color:T.muted}}>Best market <b style={{color:T.text}}>{game.bestMarket||"—"}</b></span>
        <span style={{fontSize:10,color:T.muted}}>{game.stadium}</span>
      </div>
      {game.topPlay && (
        <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${T.border}`,fontSize:12,color:T.muted2}}>
          Top play: <b style={{color:T.text}}>{game.topPlay.name}</b> <span style={{color:T.muted}}>{game.topPlay.market}</span> <span style={{color:playColor(game.topPlay.prob||0, game.topPlay),fontWeight:800,fontFamily:T.mono}}>{pct(game.topPlay.prob||0)}</span>
        </div>
      )}
    </button>
  )
}
